import React, { useMemo, useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import DesignCard from "../components/DesignCard";
import { getAllDesigns, CATEGORIES } from "../data/catalog";
import { useCatalog } from "../context/CatalogContext";
import { genderOf } from "../lib/gender";

/**
 * StylePage supports both fixed presets AND user-adjustable weight range.
 * URL: /style/:styleSlug?min=<g>&max=<g>
 */
const STYLE_PRESETS = {
  minimal:      { label: "Minimal",         tagline: "Delicate & light pieces.",            min: 0,   max: 15  },
  "daily-wear": { label: "Daily Wear",      tagline: "Light, wearable pieces you can style every day.", min: 0, max: 15 },
  gifting:      { label: "Gifting",         tagline: "Under 10 g — perfectly boxed presents.", min: 0, max: 10  },
  birthday:     { label: "Birthday Gift",   tagline: "Thoughtful, wearable birthday jewellery.", min: 0, max: 10 },
  anniversary:  { label: "Anniversary Gift",tagline: "Timeless pieces to mark the year.",   min: 0,   max: 10  },
  statement:    { label: "Statement",       tagline: "Bold, ornate pieces — heavier than 25 g.", min: 25, max: 150 },
  everyday:     { label: "Everyday Wear",   tagline: "Comfort weight — 5 to 20 g.",         min: 5,   max: 20  },
  new:          { label: "New Arrivals",    tagline: "The latest pieces added to our catalogue.", min: 0, max: 150, newestFirst: true },
};

const PAGE_SIZE = 24;
const ABS_MIN = 0;
const ABS_MAX = 150;

export default function StylePage() {
  const { styleSlug } = useParams();
  const preset = STYLE_PRESETS[styleSlug];
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    customDesigns,
    hiddenIds,
    hideDefaults,
  } = useCatalog();
  const [shown, setShown] = useState(PAGE_SIZE);

  const initialMin = Number(searchParams.get("min")) || (preset ? preset.min : 0);
  const initialMax = Number(searchParams.get("max")) || (preset ? preset.max : ABS_MAX);
  const initialMetal = searchParams.get("metal") || "all";
  const initialGender = searchParams.get("gender") || "all";
  const [minW, setMinW] = useState(initialMin);
  const [maxW, setMaxW] = useState(initialMax);
  const [metal, setMetal] = useState(initialMetal);
  const [gender, setGender] = useState(initialGender);

  // sync slider → URL (so admin can share a link with a specific range)
  useEffect(() => {
    const params = {};
    if (minW !== (preset ? preset.min : 0)) params.min = String(minW);
    if (maxW !== (preset ? preset.max : ABS_MAX)) params.max = String(maxW);
    if (metal && metal !== "all") params.metal = metal;
    if (gender && gender !== "all") params.gender = gender;
    setSearchParams(params, { replace: true });
    setShown(PAGE_SIZE);
  }, [minW, maxW, metal, gender, preset, setSearchParams]);

  const results = useMemo(() => {
    if (!preset) return [];
    const defaults = hideDefaults
      ? []
      : getAllDesigns().filter((d) => !hiddenIds.includes(d.id));
    let merged = [...customDesigns, ...defaults];
    if (preset.newestFirst) {
      merged = [...customDesigns, ...defaults];
    }
    return merged.filter((d) => {
      const w = Number(d.weight) || 0;
      if (w < minW || w > maxW) return false;
      if (metal !== "all") {
        const catSlug = d.category?.slug || d.category;
        if (catSlug !== metal) return false;
      }
      if (gender !== "all") {
        if (genderOf(d) !== gender) return false;
      }
      return true;
    });
  }, [preset, customDesigns, hiddenIds, hideDefaults, minW, maxW, metal, gender]);

  if (!preset) {
    return (
      <div className="alankar-site">
        <Header />
        <section className="cat-hero">
          <div className="wrap">
            <h1>Style not found</h1>
            <p><Link to="/">← Back to home</Link></p>
          </div>
        </section>
        <Footer />
        <FloatingWhatsApp />
      </div>
    );
  }

  return (
    <div className="alankar-site">
      <Header />
      <section className="cat-hero">
        <div className="wrap">
          <span className="eyebrow" style={{ color: "#F3D9A8" }}>
            Style · {results.length} designs
          </span>
          <h1 data-testid="style-title">{preset.label}</h1>
          <p>{preset.tagline}</p>
        </div>
      </section>

      <section className="cat-body">
        <div className="wrap">
          {/* Weight range slider + metal picker */}
          <div className="style-slider" data-testid="style-slider">
            <div className="style-slider-head">
              <strong>Weight range</strong>
              <span>
                {minW} g – {maxW} g
              </span>
            </div>
            <div
              className="style-gender-row"
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                margin: "0 0 10px",
                alignItems: "center",
              }}
              data-testid="style-gender-row"
            >
              <span style={{ fontSize: 13, color: "#7a5433", marginRight: 6 }}>
                For:
              </span>
              {[
                { slug: "all", label: "Everyone" },
                { slug: "ladies", label: "Ladies" },
                { slug: "men", label: "Men" },
                { slug: "unisex", label: "Unisex" },
                { slug: "kids", label: "Kids" },
              ].map((g) => (
                <button
                  key={g.slug}
                  type="button"
                  className={`btn ${gender === g.slug ? "btn-primary" : "btn-outline"}`}
                  style={{ padding: "6px 14px", fontSize: 13 }}
                  onClick={() => setGender(g.slug)}
                  data-testid={`style-gender-${g.slug}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <div
              className="style-metal-row"
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                margin: "10px 0 16px",
                alignItems: "center",
              }}
              data-testid="style-metal-row"
            >
              <span style={{ fontSize: 13, color: "#7a5433", marginRight: 6 }}>
                Show:
              </span>
              {[{ slug: "all", label: "All jewellery" }, ...CATEGORIES.map((c) => ({ slug: c.slug, label: c.label }))].map((m) => (
                <button
                  key={m.slug}
                  type="button"
                  className={`btn ${metal === m.slug ? "btn-primary" : "btn-outline"}`}
                  style={{ padding: "6px 14px", fontSize: 13 }}
                  onClick={() => setMetal(m.slug)}
                  data-testid={`style-metal-${m.slug}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="style-slider-body">
              <label>
                Min
                <input
                  type="range"
                  min={ABS_MIN}
                  max={ABS_MAX}
                  step={1}
                  value={minW}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setMinW(v);
                    if (v > maxW) setMaxW(v);
                  }}
                  data-testid="style-min-slider"
                />
              </label>
              <label>
                Max
                <input
                  type="range"
                  min={ABS_MIN}
                  max={ABS_MAX}
                  step={1}
                  value={maxW}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setMaxW(v);
                    if (v < minW) setMinW(v);
                  }}
                  data-testid="style-max-slider"
                />
              </label>
              <button
                type="button"
                className="btn btn-outline style-reset"
                onClick={() => {
                  setMinW(preset.min);
                  setMaxW(preset.max);
                  setMetal("all");
                  setGender("all");
                }}
                data-testid="style-reset"
              >
                Reset
              </button>
            </div>
          </div>

          {results.length === 0 ? (
            <p className="admin-muted" data-testid="style-empty">
              No designs match this weight range right now. Try widening it.
            </p>
          ) : (
            <>
              <div className="design-grid">
                {results.slice(0, shown).map((d, i) => (
                  <motion.div
                    key={d.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
                    viewport={{ once: true, amount: 0.15 }}
                  >
                    <DesignCard design={d} />
                  </motion.div>
                ))}
              </div>
              {shown < results.length && (
                <div style={{ textAlign: "center", marginTop: 32 }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setShown((s) => s + PAGE_SIZE)}
                    data-testid="style-load-more"
                  >
                    Show more designs ({results.length - shown} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
